/* MINA 6-Pack — UpCart drawer. Runs on every page, in EVERY market; the drawer is global.
 *
 * A 6-Pack line is a 6-Pack VARIANT at an EXACT even quantity that is one-time or on its own
 * 12-month plan (isSixPack). Not on line properties: those make UpCart wipe the cart.
 *
 * Two jobs, with different reach. The 6-Pack card, badge and by-twos stepper exist only where the
 * 6-Pack is sold (CA, US). The 12-MONTH-PLAN GUARD runs everywhere, because the plans are offered
 * everywhere: it strips the plan from non-6-Pack lines' dropdowns and moves any non-6-Pack line
 * already on it back to the 6-month plan, reading the cart itself rather than trusting the DOM.
 *
 * No ids are hardcoded. layout/theme.liquid renders the variant->plan map out of the same
 * custom.six_pack_config metafield the PDP reads, so an empty or missing metafield makes this whole
 * file no-op -- the correct fail-closed behaviour for a cosmetic layer.
 */
(() => {
  const cfgEl = document.querySelector('script[data-six-pack-map]');
  const MAP = new Map();
  ((cfgEl && cfgEl.getAttribute('data-six-pack-map')) || '')
    .split(',')
    .filter(Boolean)
    .forEach((pair) => {
      const parts = pair.split(':');
      /* BOTH halves or neither. layout/theme.liquid appends a pair only inside `if sp_cart_pid`,
       * so a variant id with no plan id cannot come out of the Liquid that feeds this -- which
       * made the earlier `? Number(parts[1]) : null` arm, and the `.filter(p => p !== null)` that
       * existed to clean up after it, both unreachable. Requiring both halves keeps exactly the
       * same fail-closed behaviour against a hand-mangled attribute and carries no dead branch. */
      if (/^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
        MAP.set(Number(parts[0]), Number(parts[1]));
      }
    });

  /* STRIP: every 12-month plan id, from EVERY market (layout/theme.liquid). Distinct from MAP on
   * purpose. MAP says where a 6-Pack EXISTS -- CA and US only -- and drives the badge and the
   * stepper. STRIP says which plans must never sit on a line that is not a 6-Pack, and that is true
   * in every market. *This file used to derive its plan list from MAP and return early on an empty
   * MAP -- so in the UK, where MAP is empty, the whole drawer guard switched itself off while Loop
   * offered the 12-month plan anyway (seen live 2026-09-11: a single UK 3-Pack at the yearly rate).*
   *
   * FALLBACK: variant -> its single non-12-month plan (the 6-month), read off the product's own
   * allocations in Liquid. Where a wrongly-planned line is moved to. Absent => leave it alone. */
  const numbers = (attr) => ((cfgEl && cfgEl.getAttribute(attr)) || '')
    .split(',').map((s) => s.trim()).filter((s) => /^\d+$/.test(s));
  const STRIP = numbers('data-six-pack-strip');
  const pairs = (attr) => {
    const m = new Map();
    ((cfgEl && cfgEl.getAttribute(attr)) || '').split(',').forEach((pair) => {
      const parts = pair.split(':');
      if (/^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) m.set(Number(parts[0]), parts[1]);
    });
    return m;
  };
  const FALLBACK = pairs('data-six-pack-fallback');
  /* GUARD_VARIANTS: the 6-Pack-capable variant ids in EVERY market (layout/theme.liquid). Distinct
   * from MAP for the same reason STRIP is: MAP is per-market and says where a 6-Pack is SOLD, so it
   * is empty outside CA/US, while a MINA 3-Pack row can appear in a drawer in any market. Used for
   * exactly one decision -- whether an upgrade control we cannot read sits on a MINA row. Added
   * 2026-09-22; that test used MAP before, so it was off in every market but CA/US. */
  const GUARD_VARIANTS = new Set(numbers('data-six-pack-variants').map(Number));

  /* Is this variant one the 12-month plans can sit on? A named function rather than an inline
   * expression so it can be lifted out and tested against this file's own text -- the UK case it
   * exists for cannot be reached through the drawer harness's CA/US config. Falls back to MAP when
   * the attribute is absent (an older cached page): CA/US behaviour rather than none. */
  function guardedVariant(v) {
    return GUARD_VARIANTS.size ? GUARD_VARIANTS.has(v) : MAP.has(v);
  }
  /* YEAR: variant -> its 12-month plan, but ONLY where Liquid saw that plan actually ALLOCATED to
   * the variant. The upgrade takeover keys off this, never off MAP -- MAP comes from the metafield
   * alone and stays populated while the plans are unmapped, when taking over UpCart's button would
   * cancel a working control and then fail on Shopify's 422. */
  const YEAR = pairs('data-six-pack-year');

  /* Nothing to guard and nothing to badge. With the 12-month plans known this now runs in EVERY
   * market; MAP being empty only means no 6-Pack is drawn here, which is correct outside CA/US. */
  if (!STRIP.length && !MAP.size) return;

  const SIX_PACK_VARIANTS = Array.from(MAP.keys());
  /* Kept under its old name for the code below; it is now the ALL-MARKETS list. */
  const SIX_PACK_PLANS = STRIP;

  /* UpCart CAN render into a ShadowRoot, which document.querySelectorAll cannot see. Every shipped
   * query in this repo goes through this accessor -- snippets/sf-sub-bundle-cart.liquid:236, and the
   * two-root form at :516-518. Getting this wrong is why v1 of spec §7A-7's probe would have
   * printed "NONE" with the drawer plainly open.
   *
   * spec §7A-7, 2026-09-04: on this store TODAY UpCart renders in the LIGHT DOM --
   * .upcart-product-item matched 3 in document and 0 in all ten shadow roots. Keep both roots
   * anyway. The accessor costs nothing when the shadow root is absent, and an UpCart release that
   * moves back into one would otherwise break this file, Task 6 and Task 7 in the same deploy. */
  const roots = () => {
    const out = [document];
    const sr = window.upcartDocumentOrShadowRoot;
    if (sr && sr !== document) out.push(sr);
    return out;
  };

  /* `.upcart-product-item` is the shipped row selector
   * (snippets/sf-sub-bundle-cart.liquid:410, :573, :837), CONFIRMED at spec §7A-7 on 2026-09-04:
   * 3 matches against a 3-line cart. The row also carries a hashed CSS-module class,
   * styles_ProductRow__product__, which must NEVER be used as a hook -- it changes on any UpCart
   * release. upcart-product-item is the stable half. */
  const ROW_SELECTOR = '.upcart-product-item';

  /* ONE technique, because spec §7A-7 closed 2026-09-04: the row's `id` IS the Shopify line key,
   * `<variant id>:<32 hex>`, so the variant id is the key's own prefix.
   *
   * *This replaces three candidates tried in order, written while the probe had not run: a
   * `data-variant-id` attribute, which these rows do not carry -- their only attributes are `id`
   * and `class` -- and `?variant=` parsed out of a product href, which is now never needed. Spec
   * §8's ban on href parsing therefore stands UNNARROWED; do not reintroduce that branch.*
   *
   * The shape test is lineKeyOf()'s -- the same pattern, a different capture group -- so the two
   * cannot disagree about which rows are identifiable. Returns 0 when the id is not a line key, and
   * 0 means no badge -- never a guess. */
  function variantOf(row) {
    const m = String(row.id || '').match(/(\d+):[0-9a-f]{32}/i);
    return m ? Number(m[1]) : 0;
  }

  /* `.upcart-product-quantity-input` is the confirmed read (spec §7A-7): its `.value` read back
   * 1, 1, 2 against cart quantities 1, 1, 2. It is named FIRST and by its stable `upcart-` class,
   * never by the hashed `styles_` class beside it. The generic input and the text scrape stay as
   * fallbacks for an UpCart release that renames it. */
  function qtyOf(row) {
    const input = row.querySelector(
      '.upcart-product-quantity-input, input[type="number"], input[inputmode="numeric"]'
    );
    if (input) {
      const n = parseInt(input.value, 10);
      if (n > 0) return n;
    }
    const el = row.querySelector('[class*="quantity" i]');
    const n2 = el ? parseInt(String(el.textContent || '').replace(/\D+/g, ''), 10) : NaN;
    return n2 > 0 ? n2 : 0;
  }

  /* CARD CHROME, ported from origin/Toby-Rock:snippets/bundle-cart-card.liquid -- the SGWS annual
   * bundle's card, in this same drawer, against this same app, already shipped. Two deliberate
   * departures:
   *
   *   - NO FOOTER. That card's footer is a stepper and a Remove for the GROUP, and exists only
   *     because the bundle is two lines whose own controls it hides. A 6-Pack is ONE line that
   *     keeps both of its own, and hiding its stepper would break Task 6, whose interception keys
   *     on exactly that control.
   *   - It does not hide the row's trash or stepper, for the same reason.
   *
   * The pill does NOT say "Bundle". A 6-Pack is deliberately not a bundle -- a selling plan cannot
   * coexist with any bundle mechanism, which is the constraint this entire design routes around --
   * so borrowing that word would contradict the thing the project is most careful about. The label
   * instead explains the row beneath it, which is the actual confusion the card exists to fix: that
   * line legitimately reads "3-Pack" at quantity 2.
   *
   * The header is a SIBLING before the row, never a child. UpCart owns the row's internals and
   * rewrites them; a sibling survives that and is cheap to reconcile.
   *
   * ROUND 2 (Graeme's cart mockup; spec §5.3.3 decision 8, plan Task 12, decided by Sanjeet
   * 2026-09-16). The card and its header strip STAY -- the mockup drops them, Sanjeet kept them --
   * and the header gains what the mockup puts around the product title:
   *
   *     [6-Pack] [(icon) Subscribe & Save]      <- second pill ONLY on a line that is on a plan
   *     6 filters · Every 12 months             <- cadence ONLY on a line that is on a plan
   *     (i) Ships as 2 × 3-Pack
   *
   * Everything lands in the header rather than under the title because the title belongs to
   * UpCart's row, which it rewrites (see above). The mockup's order is kept; only the container
   * differs. */

  /* THE CARD'S WORDS, as one PURE function -- no DOM, no cartState, nothing from the enclosing
   * scope -- so the exact strings can be lifted out of this file and tested on their own.
   *
   *   qty       the line's quantity, the SAME number apply() badges with (see its call site)
   *   planName  the line's OWN selling plan name from /cart.js, or null for a one-time line
   *
   * The cadence is the plan NAME, VERBATIM. Never hardcoded and never derived from the plan id:
   * Task 13 renames the Loop plans to "Every 12 months", and reading the name means this line follows
   * that rename -- and checkout, which shows the same name -- with no edit here. Until the rename it
   * shows whatever the plan is called today, which Sanjeet accepted. It is trimmed and nothing else,
   * and an empty or missing name drops the separator with it, so a nameless plan can never render as
   * a dangling "6 filters · ".
   *
   * "Ships as 2 × 3-Pack" is Graeme's word order (it used to read "Ships as 3-Pack × 2"). The count
   * is the LINE QUANTITY -- the number of 3-Packs that actually ship -- not the 6-Pack count the pill
   * shows, so at quantity 4 the pill says "6-Pack × 2" and this says "Ships as 4 × 3-Pack". Both are
   * true, and they answer different questions. */
  /* WHICH TEXT NAMES A PLAN'S CADENCE. The plan's FREQUENCY OPTION value first, its name only as a
   * fallback -- exactly the rule Loop's own dropdown uses (loop_widget.js generateLoopWidgetSellingPlan:
   * `sp.options[0].value ?? sp.name`). Corrected 2026-09-18: this read `name` alone. For the 2- and
   * 6-month plans the two are identical ("Deliver Every 6 Months"), but they need not be for the
   * 12-month plans -- the handoff records an internal name ("12 mo Fluoride") and a customer-facing
   * rename history, while Loop's dropdown showed the 12-month plan as "Deliver every year" on
   * 2026-09-09, which is the option value. The option value is the customer-facing frequency text.
   * Pure, so it is tested against this file's own text. */
  function planLabel(sp) {
    if (!sp) return null;
    const opt = Array.isArray(sp.options) && sp.options[0];
    if (opt && typeof opt.value === 'string' && opt.value.trim()) return opt.value;
    return typeof sp.name === 'string' && sp.name.trim() ? sp.name : null;
  }

  function cardText(qty, planName) {
    const name = typeof planName === 'string' ? planName.trim() : '';
    const filters = qty * 3 + ' filters';
    return {
      filters: name ? filters + ' \u00B7 ' + name : filters,
      ships: 'Ships as ' + qty + ' \u00D7 3-Pack'
    };
  }

  /* The two icons are DECORATION ONLY: aria-hidden, unfocusable, no title, no tooltip, no handler
   * (Sanjeet 2026-09-16 -- the info icon was explicitly NOT to become a tooltip). The text beside
   * each already says everything, so a screen reader loses nothing by skipping them.
   *
   * Built with createElementNS from these CONSTANT shapes, never from a markup string, and nothing
   * from the cart ever reaches them. Drawn here on a 16-unit grid rather than copied from an icon
   * set: the arrows are two 140-degree arcs of one r=5.5 circle, each the other rotated 180 degrees
   * about the centre, with a chevron at each leading end. */
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const ICON_RENEW = [
    ['path', { d: 'M2.58 7.04A5.5 5.5 0 0 1 12.76 5.25' }],
    ['path', { d: 'M13.41 2.84L12.76 5.25L10.35 4.61' }],
    ['path', { d: 'M13.42 8.96A5.5 5.5 0 0 1 3.24 10.75' }],
    ['path', { d: 'M2.59 13.16L3.24 10.75L5.65 11.39' }]
  ];
  const ICON_INFO = [
    ['circle', { cx: '8', cy: '8', r: '6.5' }],
    ['path', { d: 'M8 7.25V11.25' }],
    ['circle', { cx: '8', cy: '4.85', r: '0.95', fill: 'currentColor', stroke: 'none' }]
  ];

  function icon(shapes) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    /* width/height as ATTRIBUTES as well as in CSS: an SVG with neither renders at 300x150, so if
     * the drawer's <style> block ever fails to load, the icon degrades to small, not to enormous. */
    [['class', 'sp-6h-icon'], ['viewBox', '0 0 16 16'], ['width', '12'], ['height', '12'],
     ['fill', 'none'], ['stroke', 'currentColor'], ['stroke-width', '1.5'],
     ['stroke-linecap', 'round'], ['stroke-linejoin', 'round'],
     ['aria-hidden', 'true'], ['focusable', 'false']]
      .forEach((a) => svg.setAttribute(a[0], a[1]));
    shapes.forEach((s) => {
      const el = document.createElementNS(SVG_NS, s[0]);
      Object.keys(s[1]).forEach((k) => el.setAttribute(k, s[1][k]));
      svg.appendChild(el);
    });
    return svg;
  }

  function span(cls) {
    const el = document.createElement('span');
    el.className = cls;
    return el;
  }

  /* The text sits in its OWN span beside the icon. Writing textContent on a node that also holds
   * the icon would delete the icon on the first update. */
  function buildHeader() {
    const hdr = document.createElement('div');
    hdr.className = 'sp-6pack-header';
    const pills = document.createElement('div');
    pills.className = 'sp-6h-pills';
    pills.appendChild(span('sp-6h-tag'));
    const label = span('sp-6h-label');
    label.appendChild(icon(ICON_INFO));
    label.appendChild(span('sp-6h-label-text'));
    hdr.appendChild(pills);
    hdr.appendChild(span('sp-6h-filters'));
    hdr.appendChild(label);
    return hdr;
  }

  function buildSubscribePill() {
    const pill = span('sp-6h-sub');
    pill.appendChild(icon(ICON_RENEW));
    const t = span('sp-6h-sub-text');
    t.textContent = 'Subscribe & Save';
    pill.appendChild(t);
    return pill;
  }

  /* EVERY write is compare-then-set, and the pill is added or removed only when its presence is
   * WRONG. That is what stops a render loop, not tidiness: this file's MutationObserver watches
   * childList across the drawer, and assigning textContent replaces the text node -- a childList
   * mutation -- even when the string is identical. An unconditional write would re-schedule apply()
   * forever. With these guards the second pass writes nothing and the observer goes quiet. */
  const setText = (node, text) => { if (node.textContent !== text) node.textContent = text; };

  /*   tagText     "6-Pack" / "6-Pack × N"
   *   text        cardText()'s { filters, ships }
   *   subscribed  whether /cart.js has this line on a selling plan -- never read off the drawer */
  function applyCard(row, tagText, text, subscribed) {
    /* COMPARE-THEN-SET, dropCard()'s mirror image and measured the same way: with a bare
     * classList.add() a settled 6-Pack row took one `class` write per sweep, on a node UpCart's
     * React tree owns, although the marker was already there. Chrome emits a mutation record for
     * add() of a token the element already carries -- confirmed in the headless harness for this
     * change, where the idle-sweep write count fell from 7 (one per row) to 0 once this and
     * dropCard() were guarded. */
    if (!row.classList.contains('sp-6pack')) row.classList.add('sp-6pack');

    let hdr = row.previousElementSibling;
    if (hdr && !hdr.classList.contains('sp-6pack-header')) hdr = null;
    /* A header missing any part is rebuilt rather than patched. Nothing in this file produces one,
     * but reconciling a half-header part by part would mean re-deriving the order of its parts in
     * every branch; replacing it keeps one construction path. */
    if (hdr && !(hdr.querySelector('.sp-6h-pills') && hdr.querySelector('.sp-6h-tag') &&
                 hdr.querySelector('.sp-6h-filters') && hdr.querySelector('.sp-6h-label-text'))) {
      hdr.remove();
      hdr = null;
    }
    const fresh = !hdr;
    if (fresh) hdr = buildHeader();

    setText(hdr.querySelector('.sp-6h-tag'), tagText);
    setText(hdr.querySelector('.sp-6h-filters'), text.filters);
    setText(hdr.querySelector('.sp-6h-label-text'), text.ships);

    /* After the 6-Pack pill, so it always sits second, as in the mockup. Removed outright on a
     * one-time line rather than hidden, so no "Subscribe & Save" is left in the DOM for a screen
     * reader to announce on a line that is not subscribed. */
    const pills = hdr.querySelector('.sp-6h-pills');
    const sub = pills.querySelector('.sp-6h-sub');
    if (subscribed && !sub) pills.appendChild(buildSubscribePill());
    else if (!subscribed && sub) sub.remove();

    /* A new header is filled while DETACHED and inserted once: one mutation, not five. */
    if (fresh && row.parentNode) row.parentNode.insertBefore(hdr, row);
  }

  /* COMPARE-THEN-SET, for the reason given above setText(): apply() calls this for EVERY row that
   * is not a 6-Pack on EVERY sweep, which on this store is almost every row in the drawer. A bare
   * classList.remove() re-serializes `class` on a node UpCart's React tree owns each time, so an
   * ordinary Shower Filter row took a pointless attribute write -- and a mutation record our own
   * observer then had to read -- on every pass. The header removal below was already conditional. */
  function dropCard(row) {
    if (row.classList.contains('sp-6pack')) row.classList.remove('sp-6pack');
    const prev = row.previousElementSibling;
    if (prev && prev.classList.contains('sp-6pack-header')) prev.remove();
  }

  /* "Pack Size: 3-Pack" on a row this card has just labelled a 6-Pack is the most confusing line in
   * the drawer, precisely because it is TRUE -- the SKU really is the 3-Pack -- while reading as
   * wrong. Graeme asked for it on 2026-09-08: hide Pack Size, KEEP Filtration Type, which is the one
   * line telling the two products apart when both are in the cart. The header already says
   * "Ships as 2 × 3-Pack", so no information is lost.
   *
   * MATCHED ON TEXT, NOT ON A CLASS, deliberately. UpCart's variant-option markup has never been
   * observed on this store. The one precedent available -- `.upcart-item-property`, from
   * snippets/hide-internal-cart-properties.liquid:17 -- hides LINE-ITEM PROPERTIES, which is not the
   * same thing as a variant option; reusing it here would be a guess wearing a citation. The option
   * NAME is the reliable half: snippets/six-pack-picker.liquid:59-64 already asserts Shopify's
   * option names and renders nothing unless they are exactly "Pack Size" and "Filtration Type", so
   * this file cannot disagree with the storefront about the string.
   *
   * FAILS OPEN, ON PURPOSE -- the opposite of this file's money rule, and for a reason. Every
   * ambiguous read leaves the line VISIBLE. A redundant-but-true line costs a shopper nothing;
   * hiding the wrong element inside someone else's drawer could take the price, the stepper or the
   * whole row with it. stripSixPackPlanOption() fails the other way ("hide anyway") because a leak
   * there costs money every delivery. Cosmetics do not get that budget.
   *
   * UNVERIFIED against a rendered drawer -- confirm in the next mapped-plans window. If UpCart turns
   * out to expose a stable per-option class, prefer it and keep this text test as the guard. */
  /* The trailing class is `[^\s:–—-]`, not `\S`, and that is not fussiness. With a bare `\S` the
   * optional separator can match nothing and `\S` then consumes the colon itself, so a label-only
   * `<span>Pack Size:</span>` matches, gets hidden as if it were the whole line, and leaves its
   * "3-Pack" value stranded on the row with no label. Caught by the case table, not by reading. */
  const PACK_SIZE_RE = /^\s*pack\s*size\b\s*[:–—-]?\s*[^\s:–—-]/i;
  const FILTRATION_RE = /filtration\s*type/i;

  function hidePackSizeLine(row, on) {
    if (!on) {
      row.querySelectorAll('.sp-hide-option').forEach((el) => el.classList.remove('sp-hide-option'));
      return;
    }
    /* Already applied. Without this the walk below would run for every row on every drawer
     * mutation, and UpCart re-renders its rows constantly. */
    if (row.querySelector('.sp-hide-option')) return;

    const hits = [];
    row.querySelectorAll('*').forEach((el) => {
      if (PACK_SIZE_RE.test((el.textContent || '').trim())) hits.push(el);
    });

    /* Every ANCESTOR of the line matches too -- its text merely CONTAINS the line. Keep the deepest
     * hit, the one containing no other hit, so we hide one option row and never the block holding
     * both options, nor the row itself. The regex requires a VALUE after the label, which is what
     * stops a bare `<span>Pack Size:</span>` label from being mistaken for the whole line and
     * leaving its "3-Pack" behind, orphaned. */
    const leaves = hits.filter((el) => !hits.some((o) => o !== el && el.contains(o)));
    if (leaves.length !== 1) return;

    /* One element carrying BOTH options -- a <br>-separated block, say -- is not safe to hide:
     * Filtration Type is explicitly the line Graeme asked to keep. */
    if (FILTRATION_RE.test(leaves[0].textContent || '')) return;

    leaves[0].classList.add('sp-hide-option');
  }

  /* ---- The cart's own record of each line's plan ---------------------------------------------
   * The single source of truth for which PLAN a line is on. The drawer is a rendering of the cart
   * and does not reliably show a line's plan -- which is how a line ALREADY on the 12-month plan
   * slipped past the <select> sweep below, whose opt.selected guard never touches the chosen
   * option. Added 2026-09-11.
   *
   * Read only when the drawer's SET OF LINES changes (each row's line key plus its quantity), never
   * on every mutation: this file's own badges and classes mutate the drawer constantly, and a
   * /cart.js read per mutation would be a request storm. A line key includes the selling plan, so a
   * plan change alone still changes the signature. */
  const cartState = new Map();
  let lastSig = '';
  let syncing = false;

  function drawerSig() {
    const parts = [];
    roots().forEach((r) => {
      r.querySelectorAll(ROW_SELECTOR).forEach((row) => parts.push(row.id + '@' + qtyOf(row)));
    });
    return parts.sort().join('|');
  }

  /* A line is a 6-Pack only if it is a 6-Pack variant, at an EXACT even quantity, AND one-time or on
   * ITS OWN 12-month plan. The plan half is new (2026-09-11) and needs the cart's data, so until
   * cartState has been read the rule is variant + even quantity, as it always was.
   *
   * What the plan half fixes. "Two 3-Packs every six months" is a real, legitimate order a shopper
   * can place from the ordinary 3-Pack page, and it is byte-for-byte the same cart line as a
   * "6-Pack on six months" -- no line property can tell them apart on this store. It is therefore
   * no longer badged "6-Pack" (the mislabel this file used to accept knowingly), which in turn keeps
   * it out of the by-twos stepper and out of the 6-Pack-only dropdown rules. */
  function isSixPack(row, v, q) {
    if (SIX_PACK_VARIANTS.indexOf(v) === -1 || q < 2 || q % 2 !== 0) return false;
    const cs = cartState.get(row.id);
    if (!cs) return true;
    return cs.plan === null || cs.plan === String(MAP.get(v));
  }

  function apply() {
    roots().forEach((r) => {
      r.querySelectorAll(ROW_SELECTOR).forEach((row) => {
        const v = variantOf(row);
        const q = qtyOf(row);
        /* EXACT multiple only. At quantity 3 there is no 6-Pack and the card must be absent --
         * without that, nine filters would read as six.
         *
         * THE OLD "KNOWN AND ACCEPTED MISLABEL" IS FIXED (2026-09-11). This used to be variant +
         * even quantity and nothing else, so a 6-MONTH subscriber with two 3-Packs was badged
         * "6-Pack" and stepped in twos. It was left that way because excluding them needed a cart
         * read this file did not make. It makes one now -- cartState, above -- so isSixPack() also
         * requires one-time or the line's own 12-month plan. Until the first read lands, the old
         * rule applies. */
        const packs = isSixPack(row, v, q) ? q / 2 : 0;

        if (!packs) { dropCard(row); hidePackSizeLine(row, false); return; }

        /* ONE marker, not two. *An earlier version also inserted a `[data-sp-badge]` span beside
         * the product title, so the card said "6-Pack" twice: once in the header pill and once as
         * an unstyled duplicate -- `.six-pack-badge` carried no CSS anywhere in the theme, so it
         * rendered as bare text.* Graeme flagged the clutter on 2026-09-08. The duplicate is gone
         * and `.sp-6pack` on the ROW is now the single marker; applyCard()/dropCard() already
         * maintain it on exactly this condition, and the stepper and the plan sweep both read it.
         * applyCard() also reconciles the pill's text, so the create and update paths collapse.
         *
         * THE PLAN COMES FROM cartState, NEVER THE DRAWER (plan Task 12). The drawer does not
         * reliably show a line's plan -- see the note above cartState -- and a "Subscribe & Save"
         * pill on a one-time line would misstate what the shopper is paying for. Looked up by the
         * row's LINE KEY, which encodes the selling plan, so an entry found here is right for this
         * row even when the cart read that produced it is older than the drawer.
         *
         * No entry yet -- the first read has not landed, or this row's key is new since the last
         * one -- draws the card with NO pill and NO cadence rather than a guess. A changed key always
         * changes drawerSig(), so a read is already on its way, and syncCart() calls apply() the moment
         * it fills cartState, which adds whatever was withheld. Nothing here schedules a read of its
         * own, so a failed read cannot become a loop.
         *
         * The QUANTITY is the drawer's `q`, deliberately not cartState's. It is the number this pass
         * just badged with, so the pill, "6 filters" and "Ships as 2 × 3-Pack" can never disagree
         * with each other or with the stepper directly beneath them -- whereas after a step the
         * drawer shows the new quantity a whole /cart.js round-trip before cartState does. */
        const cs = cartState.get(row.id);
        applyCard(
          row,
          packs === 1 ? '6-Pack' : '6-Pack \u00D7 ' + packs,
          cardText(q, cs ? cs.planName : null),
          !!cs && cs.plan !== null
        );
        hidePackSizeLine(row, true);
      });

      /* An orphan header outlives its row whenever UpCart re-renders or the line goes away. Sweep by
       * STRUCTURE -- a header must be immediately followed by a carded row -- rather than by
       * remembering what we injected, because a re-render replaces the very nodes we would be
       * remembering. */
      r.querySelectorAll('.sp-6pack-header').forEach((h) => {
        const next = h.nextElementSibling;
        if (!next || !next.classList.contains('sp-6pack')) h.remove();
      });
    });

    /* THE CALL SITE, added 2026-09-08. *stripSixPackPlanOption() shipped with NO caller: across
     * 7037b43, 99213a4, 1bcefa4 and 80f2df6 the identifier occurs exactly once in the tree, at its
     * own declaration.* So neither of the two drawer leak surfaces it implements had ever run,
     * while this file's own comments, the handoff's leak table, MEMORY.md and the plan's Task 7
     * all recorded them as closed. That is the failure mode worth naming: a sweep that is written,
     * reviewed and documented is indistinguishable from one that runs, until something calls it.
     *
     * Here rather than inside the roots() loop, per the plan's Task 7 Step 5 ("as the last line
     * inside apply()"): the sweep skips rows this pass has badged, so it must run after the pass
     * that badges them, once, not once per root. */
    stripSixPackPlanOption();
  }

  /* UpCart re-renders its rows on every change, so observe rather than run once. Observing the
   * shadow root as well is required: mutations inside it do not surface on document.body. */
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    /* syncCart() is a no-op unless the drawer's set of lines changed -- see drawerSig(). */
    requestAnimationFrame(() => { queued = false; apply(); syncCart(); });
  };
  const observed = new WeakSet();
  function observeAll() {
    roots().forEach((r) => {
      const target = r === document ? document.body : r;
      if (!target || observed.has(target)) return;
      observed.add(target);
      new MutationObserver(schedule).observe(target, { childList: true, subtree: true });
    });
  }
  /* The shadow root does not exist until UpCart boots, so keep looking for it for a while. */
  observeAll();
  const poll = setInterval(observeAll, 500);
  setTimeout(() => clearInterval(poll), 15000);
  document.addEventListener('DOMContentLoaded', schedule);
  schedule();


  /* Step 6-Pack lines in TWOS so a 6-Pack stays a 6-Pack. Applies ONLY to rows the badge marked;
   * a plain 3-Pack stepping 1 -> 2 -> 3 must keep normal behaviour.
   *
   * WE MUTATE THE CART, NOT THE DOM. *An earlier draft of this task cancelled UpCart's own handler
   * and then wrote `input.value = ...` plus synthetic `input`/`change` events, hoping UpCart's
   * React state would observe it. React tracks the NATIVE value setter, so a programmatic
   * assignment is exactly what it does not see: the write is either ignored or painted over by the
   * next render, and having cancelled UpCart's handler first we would have been left with a dead
   * stepper -- the one outcome the fail-safe below exists to forbid.*
   *
   * Every cart mutation in this repo does the same thing instead: read the cart, POST to the cart
   * API keyed by LINE KEY, and let the drawer re-render from the response.
   *   assets/theme.js:1933-1943                  POST cart/change.js with { id: <line key>, quantity }
   *   snippets/sf-sub-bundle-cart.liquid:642-676 read cart.js, build updates[<line key>], POST
   *                                              cart/update.js, then window.upcartRefreshCart()
   *                                              (:663) -- and its own comment at :642 says why:
   *                                              "Reads the real cart rather than trusting the
   *                                              drawer DOM"
   *   assets/sf-sub-bundle-picker.js:392-395     the same updates[<line key>] shape
   * We use cart/change.js because we touch exactly one line, and we read cart.js first so the
   * arithmetic is done against the authoritative quantity rather than a rendered number.
   *
   * THE LINE KEY IS SETTLED. spec §7A-7 closed 2026-09-04: the row's `id` IS the Shopify line key,
   * so lineKeyOf() below is ONE read rather than three candidates. It can still miss -- an UpCart
   * release can rename or drop that id -- and a miss is a no-op, not a breakage.
   *
   * FAIL SAFE: nothing here targets a hashed CSS-module class -- the row is .upcart-product-item
   * and the button is matched by label or glyph (spec §7A-7 forbids the styles_ hook outright) --
   * but UpCart owns this markup and can rename or drop the row's id on any release. *An earlier
   * draft of this line said the target IS a hashed class; it is not, and the fail-safe matters for
   * the other reason.* If we cannot identify the button, or cannot identify the LINE, we do nothing
   * and let UpCart step by 1. Never leave a dead control -- a stepper that does nothing is worse
   * than one that steps by the wrong amount (spec section 5.4). Every early return below happens BEFORE preventDefault().
   */

  /* Never a hardcoded '/cart.js'; a localized market prefixes the path.
   * snippets/sf-sub-bundle-cart.liquid:237 is the same accessor in the same drawer. */
  const routeRoot = () =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* Explicit word-boundary tokens. The earlier draft used /decrease|remove|minus|-/, which matched
   * the Remove/trash control and ANY aria-label containing a hyphen -- so deleting a line would have
   * become a silent quantity edit. `remove`/`delete`/`trash` now BAIL OUT entirely: UpCart's own
   * handler runs and the line is removed, which is what the shopper asked for. */
  const INC  = /\b(increase|increment|plus|add)\b/;
  const DEC  = /\b(decrease|decrement|minus|subtract)\b/;
  const KILL = /\b(remove|delete|trash|bin)\b/;

  function directionOf(btn) {
    const label = [
      btn.getAttribute('aria-label') || '',
      btn.getAttribute('title') || '',
      btn.getAttribute('data-testid') || ''
    ].join(' ').toLowerCase();

    if (KILL.test(label)) return 0;
    if (INC.test(label)) return 1;
    if (DEC.test(label)) return -1;

    /* No usable label. Fall back to the button's own glyph, matched as the ENTIRE trimmed text --
     * never as a substring, which is how a hyphen inside a word became a decrement. */
    const text = String(btn.textContent || '').trim();
    if (text === '+') return 1;
    if (text === '-' || text === '\u2212' || text === '\u2013') return -1;
    return 0;
  }

  /* The LINE KEY, and it is the row's `id`. spec §7A-7, closed 2026-09-04 against the live drawer:
   * every .upcart-product-item carries id="<variant id>:<32 hex>", byte-identical to the `key`
   * /cart.js reports for that line. Returns '' when the id is not a line key, and '' means DO
   * NOTHING: UpCart's own handler runs and the line steps by 1.
   *
   * *This is cut down from three candidates tried in order. The two that went are a data-line-key /
   * data-key / data-line-item-key attribute on the row, and the same pair on a descendant; neither
   * matches anything on this store, so both were dead code. `data-line-key` is the in-repo
   * attribute name for this value -- snippets/quantity-selector.liquid:54 writes
   * data-line-key="{{ line_item.key }}" on the theme's own cart stepper -- which is why they were
   * tried first, and why the probe's own read went looking for them.*
   *
   * The shape test stays strict on purpose. Matching anything looser would let the row's hashed
   * CSS-module class (styles_ProductRow__product__) or a React key through and we would POST a
   * quantity against a line that does not exist. It is the same test variantOf() applies, so the
   * two cannot disagree about which rows are identifiable. */
  function lineKeyOf(row) {
    const m = String(row.id || '').match(/(\d+:[0-9a-f]{32})/i);
    return m ? m[1] : '';
  }

  /* One in-flight write at a time, exactly as snippets/sf-sub-bundle-cart.liquid does with `busy`
   * (declared :632, taken :644-645, released in its trailing .then). Without it, two fast clicks
   * both read the same pre-write cart and the second one overwrites the first with the same number.
   * A click that arrives while a write is in flight is DROPPED, not queued -- same as the sibling.
   * Dropping is acceptable here because the drawer is visibly re-rendering at that moment; a
   * silently wrong quantity would not be. */
  let stepping = false;

  /* `-` ON THE LAST 6-PACK REMOVES THE LINE. This is a DECISION, not a side effect of the
   * arithmetic, and it is why the floor below is 0 rather than 2.
   *
   * At quantity 2 the line is exactly one 6-Pack, so `-` means "one fewer 6-Pack" and one fewer than
   * one is none. `Math.max(0, 2 + -1 * 2)` posts `quantity: 0`, and quantity 0 is Shopify's own
   * idiom for removing a line -- the same POST the theme's own stepper makes
   * (assets/theme.js:1933-1943). Three reasons this is the right answer rather than the merely
   * convenient one:
   *   - it is what UpCart's `-` already does to a plain line at quantity 1, so a shopper stepping a
   *     6-Pack down and a Single down sees one behaviour, not two;
   *   - flooring at 2 would leave `-` doing NOTHING at the bottom of its range. That is the dead
   *     control this task's fail-safe forbids outright (spec section 5.4: a stepper that does
   *     nothing is worse than one that steps by the wrong amount) -- and it would be dead precisely
   *     where a shopper is most likely to press it;
   *   - flooring at 2 also removes the only way to clear a 6-Pack line from the stepper, pushing the
   *     shopper to hunt for the trash control for an action every other line offers in place.
   * The trash control itself is untouched either way: KILL bails out of directionOf() before we
   * intercept, so Remove still removes through UpCart's own handler.
   *
   * `Math.max(0, ...)` is still load-bearing. It cannot be reached from a badged row -- the badge
   * requires an even quantity >= 2 -- but a row can be re-badged from a stale render, and a negative
   * quantity is a 422 rather than a removal. */
  function stepLine(key, dir) {
    if (stepping) return;
    stepping = true;
    fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        const line = (cart.items || []).find((it) => it.key === key);
        /* The row moved under us between click and read. Do nothing rather than guess. */
        if (!line) return null;
        /* quantity 0 REMOVES the line, deliberately -- see the comment above this function. */
        return fetch(routeRoot() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: Math.max(0, line.quantity + dir * 2) })
        });
      })
      .then((res) => {
        if (!res) return;
        /* The theme's own header count, and then UpCart's own refresh -- UpCart re-renders from
         * that, not from our response. Same call the sibling makes after a direct cart POST
         * (snippets/sf-sub-bundle-cart.liquid:663). */
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
      })
      .catch((err) => { console.warn('[six-pack] step failed', err); })
      .then(() => { stepping = false; });
  }

  /* Clicks inside a shadow root are retargeted to the host by the time they reach document, so
   * e.target is useless here. composedPath() gives the real chain. */
  document.addEventListener('click', (e) => {
    const path = typeof e.composedPath === 'function' ? e.composedPath() : [e.target];
    const isEl = (n) => n && n.nodeType === 1 && typeof n.matches === 'function';

    const btn = path.find((n) => isEl(n) && n.matches('button, [role="button"]'));
    if (!btn) return;
    const row = path.find((n) => isEl(n) && n.matches(ROW_SELECTOR));
    /* `.sp-6pack` on the row, not a `[data-sp-badge]` descendant: the duplicate badge span was
     * removed 2026-09-08 and applyCard()/dropCard() maintain this class on the same condition. */
    if (!row || !row.classList.contains('sp-6pack')) return;

    const dir = directionOf(btn);
    if (!dir) return;

    /* Identify the LINE before cancelling the event. Cancelling first and then discovering we
     * cannot name the line leaves a dead control -- the exact failure the fail-safe above forbids.
     * Every return above and here happens while UpCart's own handler can still run. */
    const key = lineKeyOf(row);
    if (!key) return;

    e.preventDefault();
    e.stopPropagation();

    stepLine(key, dir);
  }, true);


  /* ---- Reading the cart, and the one automatic correction -------------------------------------
   * Mirrors the live SF Sub-Bundle card's pattern on this same drawer
   * (snippets/sf-sub-bundle-cart.liquid, updateBoth): read /cart.js, change the cart, then
   * window.upcartRefreshCart() -- UpCart re-renders from its own fetch, never from our response. */
  /* A drawer change that lands WHILE a read is in flight used to be dropped: syncCart() returned on
   * `syncing`, and if nothing else then changed, a line that had just gone onto the 12-month plan
   * was never read, so never corrected. Found in review 2026-09-11. Now it is remembered and read
   * once the current read finishes. A FAILED read does not re-schedule itself -- it waits for the
   * next real change -- so a /cart.js that keeps erroring cannot spin into a request loop. */
  let pendingSync = false;

  function syncCart() {
    if (syncing) { pendingSync = true; return; }
    const sig = drawerSig();
    if (!sig || sig === lastSig) return;
    syncing = true;
    fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        /* A 200 with no items ARRAY is not a cart (the same rule collides() applies on the PDP):
         * leave lastSig unset so the next change retries rather than trusting a non-answer. */
        if (!cart || !Array.isArray(cart.items)) return null;
        lastSig = sig;
        cartState.clear();
        cart.items.forEach((it) => {
          const sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
          /* planName (plan Task 12): the drawer card's cadence, shown verbatim. Null on a one-time
           * line, and null too if a plan arrives with no usable label -- the card then shows the
           * filter count alone rather than inventing a cadence. Read by planLabel(), below. */
          cartState.set(it.key, {
            variant: Number(it.variant_id),
            qty: Number(it.quantity),
            plan: sp ? String(sp.id) : null,
            planName: planLabel(sp)
          });
        });
        /* This apply() is also what CORRECTS a card drawn before this read landed -- it adds the
         * "Subscribe & Save" pill and the cadence that apply() withholds without a cartState entry.
         * Its DOM writes fire the observer again, and it stops there: the next apply() writes nothing
         * (every write is compare-then-set), and the next syncCart() ends at an unchanged signature --
         * our header matches no row selector and holds no quantity, so it cannot change drawerSig().
         * If that pass lands while this read is still finishing it is deferred through pendingSync,
         * which costs one more identical, equally empty pass, not a cycle. */
        apply();
        return correctOne(cart.items);
      })
      .catch((err) => { console.warn('[six-pack] cart read failed', err); })
      .then(() => {
        syncing = false;
        if (pendingSync) { pendingSync = false; schedule(); }
      });
  }

  /* THE ONE AUTOMATIC CORRECTION: a 12-month plan on a line that is NOT a 6-Pack. That is the leak
   * -- the 6-Pack's yearly rate on half the goods, $9.10 a delivery on Fluoride -- however it got
   * there: UpCart's dropdown, a cart built before this fix, the UK where no 6-Pack exists, or a
   * hand-crafted request. The line moves to its FALLBACK plan (the 6-month), which keeps the shopper
   * subscribed at a correct rate. Decided by Sanjeet 2026-09-11.
   *
   * DELIBERATELY NOT CORRECTED: a 6-Pack variant at an even quantity on the 6-MONTH plan. That line
   * is indistinguishable from two 3-Packs every six months, a legitimate order placeable from the
   * ordinary 3-Pack page, and moving it to yearly would change a real subscription out from under
   * the shopper. Only the harmful case is touched.
   *
   * One correction per read, and never the same key -> plan twice. Each change alters the line key,
   * UpCart re-renders, the signature changes, and the next read both verifies this correction and
   * takes the next one -- so they chain one at a time. A change that fails is not retried: a line
   * this cannot fix degrades to a single console warning, not a loop of failing requests. */
  const attempted = new Set();
  function correctOne(items) {
    for (const it of items) {
      const sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
      const plan = sp ? String(sp.id) : null;
      if (!plan || STRIP.indexOf(plan) === -1) continue;
      const v = Number(it.variant_id);
      const q = Number(it.quantity);
      if (MAP.has(v) && q >= 2 && q % 2 === 0) continue;   // a genuine 6-Pack, on the plan it should be
      const target = FALLBACK.get(v);
      const tag = it.key + '>' + (target || 'none');
      if (attempted.has(tag)) continue;
      attempted.add(tag);
      if (!target) {
        console.warn('[six-pack] 12-month plan on a line that is not a 6-Pack, and no single fallback plan to move it to:', it.key);
        continue;
      }
      return fetch(routeRoot() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: it.key, quantity: q, selling_plan: Number(target) })
      }).then((res) => {
        if (!res.ok) { console.warn('[six-pack] could not move a line off the 12-month plan', res.status); return; }
        /* TELL THE SHOPPER (Sanjeet, 2026-09-22). This is an unattended write to someone's cart --
         * it can fire on any page, on a cart built before this shipped -- so it should not be
         * silent. The theme's own toast is used rather than any UI of ours: `toast:show` is what
         * sections/quick-order-list.liquid raises and assets/theme.js listens for, and it fails
         * open by construction -- with no <x-toast> in the layout the event is simply unheard.
         *
         * The message is a FIXED string. assets/theme.js parses it with
         * createContextualFragment(), i.e. as HTML, so interpolating anything -- a plan name out of
         * /cart.js especially -- would put app-controlled data into an HTML parse. Nothing here
         * varies, so nothing can be injected. Once per correction: `attempted` above already makes
         * each line+target pair fire at most once per page. */
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: { message: 'We updated the delivery frequency on one of your cart items.' }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
      });
    }
    return null;
  }

  /* "UPGRADE TO SUBSCRIBE & SAVE" ON A 6-PACK ROW PUTS IT ON ITS 12-MONTH PLAN. Decided by Sanjeet
   * 2026-09-11. Since the 2026-09-08 rename merged all four plans into one "Subscribe & Save"
   * group, UpCart's own upgrade applies the FIRST plan it finds -- the 6-month -- so a one-time
   * 6-Pack upgraded in the drawer became two 3-Packs every six months at 5% off (the $132.98 this
   * project keeps as a known-wrong figure) instead of the yearly 6-Pack at $114.78. Before the
   * rename the yearly plan sat alone in its own group, so the button could only ever pick it.
   *
   * Taken over in the CAPTURE phase on document, which runs before UpCart's own handler, and only
   * for a BUTTON inside the upgrade section of a row this file badged. A lone 3-Pack keeps UpCart's
   * own upgrade, which correctly offers the 6-month. The section is matched by its stable `upcart-`
   * class -- the selector the live SF Sub-Bundle card already uses on this drawer. The line is named
   * before the event is cancelled, so a row we cannot identify is left to UpCart, never killed. */
  let upgrading = false;
  function upgradeToYear(key, plan) {
    if (upgrading) return;
    upgrading = true;
    /* EVERY outcome ends in a drawer refresh, success or not. The click has already been taken from
     * UpCart, so a failure that only logged would leave the shopper pressing a button that does
     * nothing; re-syncing puts the drawer back in step with the real cart so the next press works.
     * (Found in review 2026-09-11, alongside the YEAR gate that makes failure rare in the first
     * place.) r.ok is checked BEFORE r.json(): a non-JSON error page must not throw past it. */
    const resync = () => {
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (typeof window.upcartRefreshCart === 'function') window.upcartRefreshCart();
    };
    fetch(routeRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        const line = cart && (cart.items || []).find((it) => it.key === key);
        if (!line) return null;                      // the row moved under us; do nothing, never guess
        return fetch(routeRoot() + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: line.quantity, selling_plan: plan })
        });
      })
      .then((res) => {
        if (res && !res.ok) console.warn('[six-pack] upgrade to the 12-month plan failed', res.status);
        resync();
      })
      .catch((err) => { console.warn('[six-pack] upgrade failed', err); resync(); })
      .then(() => { upgrading = false; });
  }

  document.addEventListener('click', (e) => {
    const path = typeof e.composedPath === 'function' ? e.composedPath() : [e.target];
    const isEl = (n) => n && n.nodeType === 1 && typeof n.matches === 'function';
    const sec = path.find((n) => isEl(n) && n.matches('.upcart-subscription-upgrade-section'));
    if (!sec) return;
    const btn = path.find((n) => isEl(n) && n.matches('button, [role="button"]'));
    if (!btn || !sec.contains(btn)) return;
    const row = path.find((n) => isEl(n) && n.matches(ROW_SELECTOR));
    if (!row || !row.classList.contains('sp-6pack')) return;
    /* YEAR, not MAP: with no ALLOCATED 12-month plan the click is left to UpCart untouched. */
    const plan = YEAR.get(variantOf(row));
    const key = lineKeyOf(row);
    if (!plan || !key) return;
    e.preventDefault();
    e.stopPropagation();
    upgradeToYear(key, Number(plan));
  }, true);

  /* UpCart's in-cart subscription-upgrade <select> lists every plan the line's product is enrolled
   * in, so it offers the 6-Pack's 12-month plan on a LONE 3-Pack line (AC-16a). Strip that option.
   *
   * THE TARGET IS A WHOLE GROUP, NOT ONE OPTION IN ONE SELECTOR. Confirmed 2026-09-04 against the
   * live product JSON (spec §7A-1): Loop put each 12-month plan in its OWN selling plan group
   * rather than adding it to the existing "Subscribe & Save" group, so a 3-Pack variant belongs to
   * TWO groups. A renderer that draws one control per group hands this row an extra <select> -- or
   * an extra <optgroup> -- in which EVERY option is a 6-Pack plan. Removing those options then
   * EMPTIES the control instead of removing it, and an empty selector is precisely the dead control
   * this file forbids everywhere else (Task 6's fail-safe). The sweep below drops a container we
   * emptied ourselves, and only that one.
   *
   * Ported from origin/Toby-Rock:snippets/bundle-cart-card.liquid:213-221, whose match at :218 is
   *     !opt.selected && val.split(/[^0-9]+/).indexOf(BUNDLE_PLAN) !== -1
   * Two things about that line are load-bearing and an earlier draft dropped both:
   *   - it matches the PLAN ID inside the option's VALUE, not words in the option's TEXT. Matching
   *     /12\s*month|annual/i on text would also strip the live SF Sub-Bundle plan, whose name
   *     contains "Annual", from an unrelated line;
   *   - it never touches the SELECTED option. Removing a real subscriber's own current plan from
   *     their dropdown is a worse defect than the leak it closes.
   *
   * Client-side only. The cart API bypasses this, which is why the residual leak is accepted
   * knowingly in spec section 5.2 rather than claimed closed. */
  function stripSixPackPlanOption() {
    /* NO `if (!SIX_PACK_PLANS.length) return;` GUARD, and it would now be wrong as well as dead.
     * SIX_PACK_PLANS is the ALL-MARKETS list (STRIP) since 2026-09-11 -- no longer MAP's values --
     * and the file returns at the top only when BOTH it and MAP are empty. An empty list here simply
     * matches nothing below, which is the correct behaviour; a guard would read as a live safety
     * check and invite someone to "restore symmetry" by adding the same test to apply(). */

    /* SCOPED TO DRAWER ROWS, deliberately. *An earlier draft queried `option[value]` across each
     * whole root on every drawer mutation* -- which is `document` as well as the shadow root, so a
     * `<select>` anywhere on the page whose option value happened to contain the plan id would have
     * been edited, contradicting this file's own "UpCart drawer only" contract. Starting from
     * ROW_SELECTOR makes the scope structural rather than a per-option `closest()` check, and the
     * null branch that check needed disappears with it. */
    roots().forEach((r) => {
      r.querySelectorAll(ROW_SELECTOR).forEach((row) => {
        /* A 6-PACK ROW is the mirror image: it may keep its 12-month plan, and now LOSES the
         * 6-month instead. A 6-Pack is a yearly product (Sanjeet, 2026-09-11), so its dropdown
         * offers one-time and yearly only. Removed by EXACT id -- the variant's fallback plan, read
         * from Liquid -- so nothing is stripped on a guess about UpCart's value format. The selected
         * option is still never removed. Rows we badged are then done; everything below is for
         * rows that are NOT a 6-Pack. */
        if (row.classList.contains('sp-6pack')) {
          /* A class set while this row was NOT a 6-Pack (a lone 3-Pack at quantity 1, stepped to 2
           * with UpCart's own +) would otherwise outlive the change. The CSS is scoped to
           * :not(.sp-6pack) so it is already harmless; clearing it keeps the DOM honest too.
           *
           * THE SELECTOR IS THE COMPARE-THEN-SET GUARD here, so there is no `contains()` test to
           * add: `.sp-hide-upgrade` matches only nodes that carry the class, so every remove() this
           * line reaches is a real change, and a row with none matches nothing and writes nothing.
           * Do not "restore symmetry" with dropCard()'s explicit test -- there the node is reached
           * unconditionally, which is exactly the difference. */
          row.querySelectorAll('.sp-hide-upgrade').forEach((s) => s.classList.remove('sp-hide-upgrade'));
          const fb = FALLBACK.get(variantOf(row));
          if (fb) {
            row.querySelectorAll('option[value]').forEach((opt) => {
              if (opt.selected) return;
              if (String(opt.getAttribute('value') || '').split(/[^0-9]+/).indexOf(fb) !== -1) opt.remove();
            });
          }
          return;
        }

        /* THE UPGRADE CONTROL -- the drawer's OTHER leak surface, and the one the option sweep below
         * cannot see. UpCart renders a <select> on a line that already HAS a plan, but a prominent
         * "Upgrade to <plan name>" control on a ONE-TIME line. Observed live 2026-09-04: a lone
         * 3-Pack showed "Upgrade to MINA 6-Pack Fluoride (12mo)", which is the 18% rate on half the
         * goods -- $57.39 against $66.49, the $9.10-a-delivery leak.
         *
         * `.upcart-subscription-upgrade-section` is the container. It is a stable `upcart-` class,
         * not a hashed `styles_` one, and it is the same selector
         * origin/Toby-Rock:snippets/bundle-cart-card.liquid already hides on its bundle rows -- so
         * it is precedented against this app rather than guessed.
         *
         * SURGICAL, not blanket (decided 2026-09-05): hide it only when it actually promotes a
         * 6-Pack plan, so the legitimate 2-month / 6-month upsell survives on a lone 3-Pack.
         *
         * TWO ARMS, AND THE SECOND ONE IS SCOPED TO MINA (Sanjeet, 2026-09-22):
         *
         *   1. A POSITIVE MATCH -- a 6-Pack plan id read out of the section -- hides it on ANY row.
         *      That is the whole rule everywhere except MINA's own variants.
         *   2. NO id readable anywhere in the section hides it ONLY when the row's VARIANT is one
         *      of the 6-Pack variants (MAP). That is the cautious "cannot judge it, fail towards
         *      the money" arm, and it now applies only where the leak it defends against can
         *      exist: the 12-month plans are allocated to MINA 3-Pack variants alone, so no other
         *      product's upgrade control has a 6-Pack plan to promote in the first place.
         *
         * *WHY IT IS SCOPED NOW. Arm 2 used to run on EVERY row, and against UpCart 2-407 that is
         * not a rare fallback -- it is the normal case. Its one-time shape is a bare
         * `<button>{buttonText}</button>` inside the section (SubscriptionUpgradesModule): the plan
         * id lives in the CLOSURE, never in the markup, so `ids.length === 0` is true on every
         * one-time line in the drawer. Only the SUBSCRIBED shape, a <select> of
         * `<option value="<plan id>">`, exposes ids at all. Unscoped, this therefore hid
         * "Upgrade to Subscribe & Save" on every one-time Shower Filter, Refill, Gravity Water
         * System and bottle row in the store -- a store-wide loss of someone else's upsell, to
         * defend a leak those products cannot have. Found by a scope audit of PR #81, 2026-09-22.*
         *
         * TWO FURTHER GUARDS STILL COVER THE LEAK, which is why narrowing arm 2 is safe rather
         * than merely cheaper: the option sweep below strips every 6-Pack plan option from a
         * non-6-Pack row's dropdown, and correctOne() reads /cart.js and moves any line that is
         * not a 6-Pack off a 12-month plan onto its fallback plan whatever put it there -- a
         * dropdown, an upgrade button, an older cart or a hand-made request. Arm 2 is a display
         * courtesy in front of those two, never the thing holding the money.
         *
         * KNOWN NARROWING, named rather than glossed: MAP is PER-MARKET (layout/theme.liquid --
         * `pair[1][sp_cart_country]`), so outside CA/US it is empty and arm 2 fires nowhere,
         * including on a UK MINA 3-Pack -- and Loop does offer the 12-month plan there, because
         * selling plans are not market-scoped (seen live 2026-09-11). A UK shopper can therefore
         * still take an unlabelled upgrade onto a 12-month plan; correctOne() moves that line back
         * to the 6-month plan on the next cart read, so it costs a round trip, not money.*
         *
         * WIDENED TO EVERY MARKET 2026-09-22 (Sanjeet). The test now uses GUARD_VARIANTS, the
         * all-markets 6-Pack-capable variant list, so a UK or `other`-market MINA 3-Pack row is
         * covered too -- the plans are offered in every market even though the 6-Pack is sold in
         * two. MAP is deliberately NOT used here any more, and is still the right list for the
         * badge, the card and the stepper, which exist only where the 6-Pack is sold. FALLBACK was
         * the other candidate and was rejected: it also holds the MINA Singles, which carry no
         * 12-month plan at all (spec section 5.2, confirmed live 2026-09-04), so it is wider than
         * the leak. Falls back to MAP if the attribute is ever absent, so an older cached page
         * keeps the CA/US behaviour rather than none. */
        const minaVariantRow = guardedVariant(variantOf(row));
        row.querySelectorAll('.upcart-subscription-upgrade-section').forEach((sec) => {
          let hay = sec.textContent || '';
          sec.querySelectorAll('*').forEach((n) => {
            for (const at of n.attributes) hay += ' ' + at.value;
          });
          const ids = hay.match(/\d{6,}/g) || [];
          const promotesSixPack = ids.some((id) => SIX_PACK_PLANS.indexOf(id) !== -1);
          /* ARM 2 IS GATED ON YEAR, NOT JUST ON "this is a MINA row". *Found in the PR #81 merge
           * audit, 2026-09-22: without it the cautious arm fired while the 12-month plans were
           * UNMAPPED -- the store's normal state between test windows, and the state LIVE is in the
           * moment this merges. UpCart draws a one-time row's upgrade as a bare <button> with the
           * plan id only in a React closure (see :977), so `ids.length === 0` is the ORDINARY case
           * on such a row, not a signal. The result was that a lone one-time MINA 3-Pack lost
           * UpCart's own "Upgrade to Subscribe & Save" -- while the click interception at :877,
           * which is already gated on YEAR, had correctly stood down. The shopper got neither.*
           * YEAR means "Liquid saw the 12-month plan really ALLOCATED to this variant", so this
           * makes the hide agree with the takeover that replaces it: hide UpCart's control only
           * where we are actually able to offer the yearly upgrade in its place. Mapped, the leak
           * this arm defends against is covered exactly as before. */
          const hide = promotesSixPack || (ids.length === 0 && minaVariantRow && YEAR.has(variantOf(row)));
          /* COMPARE-THEN-SET, like setText() above and for the same reason -- except that this node
           * belongs to UpCart's React tree, so a write also costs a reconciliation there. A bare
           * classList.toggle() re-serializes `class` on every sweep even when the answer has not
           * changed, and these sweeps are driven by a MutationObserver. */
          if (sec.classList.contains('sp-hide-upgrade') !== hide) {
            sec.classList.toggle('sp-hide-upgrade', hide);
          }
        });

        const emptiedGroups = [];
        const touchedSelects = new Set();

        row.querySelectorAll('option[value]').forEach((opt) => {
          if (opt.selected) return;
          const parts = String(opt.getAttribute('value') || '').split(/[^0-9]+/);
          if (!SIX_PACK_PLANS.some((p) => parts.indexOf(p) !== -1)) return;
          const group = opt.closest('optgroup');
          const sel = opt.closest('select');
          if (group) emptiedGroups.push(group);
          if (sel) touchedSelects.add(sel);
          opt.remove();
        });

        /* The second-group sweep. Remove ONLY a container we just emptied -- an optgroup first,
         * because emptying it can in turn empty the <select> it sat in. A container that still
         * holds an option is a real control and is left alone, and a container we never touched is
         * not ours to remove. If the drawer turns out to merge both groups into one <select>, both
         * loops find nothing and this costs nothing. UNVERIFIED against a rendered drawer. */
        emptiedGroups.forEach((g) => { if (!g.querySelector('option')) g.remove(); });
        touchedSelects.forEach((s) => { if (!s.querySelector('option')) s.remove(); });

        /* KNOWN LIMIT, and Step 3's drawer row is what settles it. `opt.selected` is TRUE for the
         * first option of a <select> that carries no explicit selection, so if the drawer draws
         * the 12-month group as its own selector, its first option is skipped by the guard above,
         * one option survives, and that selector is neither emptied nor removed -- the leak stays
         * open on exactly the shape this sweep was added for. The guard is not being weakened on a
         * guess: it is what keeps a real subscriber's own current plan in their dropdown. If Step
         * 3 shows a standalone 12-month selector, the fix is to detect that control up front -- a
         * <select> every one of whose options carries a 6-Pack plan id -- and remove it whole,
         * NOT to relax `opt.selected`. */
      });
    });
  }
})();
